import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

export default function PaperAdminWorkListPage() {
  const [works, setWorks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchCategory, setBatchCategory] = useState('FESTIVAL');
  const [batchLoading, setBatchLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchWorks = (page = 1) => {
    setLoading(true);
    axios.get(`${API}/api/paperWork`, {
      params: { page, limit: 20 },
      headers,
    })
      .then(res => {
        setWorks(res.data.works || []);
        setTotalPages(res.data.totalPages || 1);
        setCurrentPage(res.data.currentPage || 1);
        setSelectedIds([]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchWorks(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`"${title}" 작품을 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API}/api/paperWork/${id}`, { headers });
      fetchWorks(currentPage);
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === works.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(works.map(w => w.id));
    }
  };

  const handleBatchCategory = async () => {
    if (selectedIds.length === 0) return alert('변경할 작품을 선택해주세요.');
    if (!window.confirm(`선택한 ${selectedIds.length}개 작품의 카테고리를 "${batchCategory === 'FESTIVAL' ? '축제' : '공연'}"(으)로 변경하시겠습니까?`)) return;
    setBatchLoading(true);
    try {
      await axios.put(`${API}/api/paperWork/batch/category`, { ids: selectedIds, category: batchCategory }, { headers });
      fetchWorks(currentPage);
    } catch {
      alert('일괄 변경 중 오류가 발생했습니다.');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return alert('삭제할 작품을 선택해주세요.');
    if (!window.confirm(`선택한 ${selectedIds.length}개 작품을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;
    setBatchLoading(true);
    try {
      await axios.delete(`${API}/api/paperWork/batch`, { data: { ids: selectedIds }, headers });
      fetchWorks(currentPage);
    } catch {
      alert('일괄 삭제 중 오류가 발생했습니다.');
    } finally {
      setBatchLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Admin Nav */}
      <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-3 overflow-x-auto">
        <Link to={`${PAPER_BASE}/admin/members`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">회원 관리</Link>
        <Link to={`${PAPER_BASE}/admin/works`} className="text-sm font-medium text-violet-400 border-b-2 border-violet-400 pb-1 whitespace-nowrap">행사 관리</Link>
        <Link to={`${PAPER_BASE}/admin/services`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">서비스 관리</Link>
        <Link to={`${PAPER_BASE}/admin/partners`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">파트너 관리</Link>
        <Link to={`${PAPER_BASE}/admin/blogs`} className="text-sm font-medium text-white/40 hover:text-white/60 transition-colors pb-1 whitespace-nowrap">스토리 관리</Link>
      </div>

      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-light text-white tracking-wide">Work 관리</h1>
        <Link
          to={`${PAPER_BASE}/admin/works/new`}
          className="flex items-center gap-2 px-5 py-2.5 bg-violet-500 hover:bg-violet-600 text-white text-sm rounded-lg transition-colors"
        >
          <Plus size={16} /> 추가
        </Link>
      </div>

      {/* Batch actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
          <span className="text-violet-400 text-sm font-medium">{selectedIds.length}개 선택됨</span>
          <select
            value={batchCategory}
            onChange={(e) => setBatchCategory(e.target.value)}
            className="bg-[#1a1428] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm outline-none"
          >
            <option value="FESTIVAL">축제</option>
            <option value="EVENT">공연</option>
          </select>
          <button
            onClick={handleBatchCategory}
            disabled={batchLoading}
            className="px-4 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            {batchLoading ? '처리 중...' : '카테고리 변경'}
          </button>
          <div className="w-px h-6 bg-white/10" />
          <button
            onClick={handleBatchDelete}
            disabled={batchLoading}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-red-500/20 hover:bg-red-500/40 disabled:opacity-50 text-red-400 text-sm rounded-lg transition-colors"
          >
            <Trash2 size={13} />
            {batchLoading ? '처리 중...' : '일괄 삭제'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20">
          <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin mx-auto" />
        </div>
      ) : works.length === 0 ? (
        <div className="text-center py-20 text-white/30">등록된 작품이 없습니다.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="py-3 px-2 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === works.length && works.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded accent-violet-500"
                  />
                </th>
                <th className="text-left py-3 px-4 text-white/40 text-xs tracking-wider uppercase font-medium">썸네일</th>
                <th className="text-left py-3 px-4 text-white/40 text-xs tracking-wider uppercase font-medium">제목</th>
                <th className="text-left py-3 px-4 text-white/40 text-xs tracking-wider uppercase font-medium hidden md:table-cell">카테고리</th>
                <th className="text-left py-3 px-4 text-white/40 text-xs tracking-wider uppercase font-medium hidden md:table-cell">날짜</th>
                <th className="text-right py-3 px-4 text-white/40 text-xs tracking-wider uppercase font-medium">액션</th>
              </tr>
            </thead>
            <tbody>
              {works.map(work => (
                <tr key={work.id} className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors ${selectedIds.includes(work.id) ? 'bg-violet-500/5' : ''}`}>
                  <td className="py-3 px-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(work.id)}
                      onChange={() => toggleSelect(work.id)}
                      className="w-4 h-4 rounded accent-violet-500"
                    />
                  </td>
                  <td className="py-3 px-4">
                    {(work.posterUrl || work.thumbnailUrl) ? (
                      <div className="w-10 aspect-[210/297] rounded-lg overflow-hidden bg-[#1a1428]">
                        <img src={work.posterUrl || work.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-10 aspect-[210/297] rounded-lg bg-[#1a1428]" />
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Link to={`${PAPER_BASE}/admin/works/${work.id}/edit`} className="text-white/80 text-sm hover:text-violet-400 transition-colors">
                      {work.title}
                    </Link>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <span className={`px-2 py-0.5 rounded text-xs ${work.category === 'EVENT' ? 'bg-green-500/15 text-green-400' : 'bg-orange-500/15 text-orange-400'}`}>
                      {work.category === 'FESTIVAL' ? '축제' : '공연'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-white/40 text-sm hidden md:table-cell">
                    {work.workDate ? (work.workDateEnd && work.workDateEnd !== work.workDate ? `${work.workDate} ~ ${work.workDateEnd}` : work.workDate) : '-'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`${PAPER_BASE}/admin/works/${work.id}/edit`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-white/30 hover:text-violet-400 hover:bg-violet-400/10 transition-all"
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        onClick={() => handleDelete(work.id, work.title)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-white/30 hover:text-red-400 hover:bg-red-400/10 transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => fetchWorks(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 transition-all"
          >
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => fetchWorks(page)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-all ${
                page === currentPage ? 'bg-violet-400/20 text-violet-400' : 'text-white/40 hover:text-white/60'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => fetchWorks(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 transition-all"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
